import React, { useState, useEffect, useMemo } from 'react';
import { Pill, Leaf, Sparkles, Zap, Brain } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatDateByCountry, formatChartDateShort } from '../utils/dateUtils';

/**
 * Supplement & Medication Timeline
 * 
 * Horizontal bar-style timeline aligned with correlation charts above.
 * MUST use exact axis config from chart - does not generate its own ticks.
 * This ensures visual alignment for correlation.
 * 
 * RECENTLY ADDED SECTION:
 * - Shows supplements added in the last 14 days prominently at the top
 * - Solves the visibility issue for short-duration items on long timelines
 * 
 * LOCKED COLOUR SYSTEM — DO NOT MODIFY
 * Colour communicates treatment category, not severity or effectiveness.
 */

// LOCKED CATEGORY COLOUR SYSTEM — Solid fills, no gradients, minimum 0.85 opacity
const CATEGORIES = {
  vitamin: { 
    label: 'Vitamin', 
    icon: Sparkles, 
    bgColor: '#F2B705',       // Amber/Yellow - LOCKED
    textColor: '#1F2937',     // Dark text for yellow bg
    iconColor: '#1F2937'
  },
  supplement: { 
    label: 'Supplement', 
    icon: Leaf, 
    bgColor: '#3FAE7F',       // Green - LOCKED
    textColor: '#FFFFFF',     // White text
    iconColor: '#FFFFFF'
  },
  medication: { 
    label: 'Medication', 
    icon: Pill, 
    bgColor: '#4F6BED',       // Blue/Indigo - LOCKED
    textColor: '#FFFFFF',     // White text
    iconColor: '#FFFFFF'
  },
  other: { 
    label: 'Other', 
    icon: Zap, 
    bgColor: '#8B5CF6',       // Purple - LOCKED
    textColor: '#FFFFFF',     // White text
    iconColor: '#FFFFFF'
  },
  prn: { 
    label: 'PRN', 
    icon: Zap, 
    bgColor: '#F59E0B',       // Orange - LOCKED
    textColor: '#1F2937',     // Dark text for orange bg
    iconColor: '#1F2937'
  },
  specialty: { 
    label: 'Specialty', 
    icon: Brain, 
    bgColor: '#7C5CFA',       // Purple - LOCKED
    textColor: '#FFFFFF',     // White text
    iconColor: '#FFFFFF'
  }
};

// Parse date string to Date object
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr);
};

// Check if a supplement is recently added (within 14 days)
const RECENT_DAYS = 14;
const isRecentlyAdded = (startDate) => {
  const startTs = parseDate(startDate)?.getTime();
  if (!startTs) return false;
  const cutoff = Date.now() - (RECENT_DAYS * 24 * 60 * 60 * 1000);
  return startTs > cutoff;
};

// Calculate bar position using LINEAR TIME positioning (same scale as chart)
// domainStartTs and domainEndTs define the exact same domain used by the chart
// Domain extends to TODAY so ongoing treatments show their full duration
const calculateBarStyleLinear = (startDate, endDate, domainStartTs, domainEndTs) => {
  const startTs = parseDate(startDate)?.getTime();
  if (!startTs || !domainStartTs || !domainEndTs) {
    return { left: '0%', width: '0%', visible: false };
  }
  
  const totalRange = domainEndTs - domainStartTs;
  if (totalRange <= 0) {
    return { left: '0%', width: '100%', visible: true };
  }
  
  // For ongoing treatments, extend to current date (domain already extends to today)
  const nowTs = Date.now();
  const endTs = endDate ? parseDate(endDate)?.getTime() : nowTs;
  
  // If treatment ends before domain starts, hide it
  if (endTs < domainStartTs) {
    return { left: '0%', width: '0%', visible: false };
  }
  
  // Clamp to domain bounds
  const clampedStart = Math.max(startTs, domainStartTs);
  const clampedEnd = Math.min(endTs, domainEndTs);
  
  // Calculate percentage positions using same linear scale as chart
  const startPos = ((clampedStart - domainStartTs) / totalRange) * 100;
  const endPos = ((clampedEnd - domainStartTs) / totalRange) * 100;
  
  // Calculate raw width as percentage
  const rawWidth = endPos - startPos;
  
  // Check if this is a recently added item
  const isNew = isRecentlyAdded(startDate);
  
  // Check if duration is very short (needs hover tooltip)
  const durationDays = (endTs - startTs) / (24 * 60 * 60 * 1000);
  const isThin = rawWidth < 8 || durationDays < 14;
  
  // Minimum width: 20px equivalent (~1.5% on typical screen) for visibility
  // But keep the START position accurate - the bar grows from that point
  const minWidthPercent = 1.5;
  const width = Math.max(minWidthPercent, rawWidth);
  
  return {
    left: `${Math.max(0, Math.min(98, startPos))}%`, // Cap at 98% so dot doesn't overflow
    width: `${Math.min(100 - startPos, width)}%`,
    visible: true,
    isNew: isNew,
    isVeryShort: durationDays < 7,
    isThin: isThin
  };
};

const SupplementTimeline = ({ 
  supplements = [], 
  axisConfig = null, // EXACT config from chart: { domainStart, domainEnd, ticks, margin }
  hoveredDate = null,
  onHoverDate,
  maxHeight = 200,
  hideCategoryFilter = false,
  selectedTreatment = null, // Currently selected treatment for chart overlay
  onTreatmentSelect = null  // Callback when treatment is clicked
}) => {
  const { user } = useAuth();
  const country = user?.country || 'AU';
  
  const [expandedId, setExpandedId] = useState(null);
  const [activeCategories, setActiveCategories] = useState(['vitamin', 'supplement', 'medication', 'other', 'prn', 'specialty']);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Format date helper using user's country
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Ongoing';
    return formatDateByCountry(dateStr, country, { includeDay: true, shortMonth: true, shortYear: true });
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use chart's exact numeric domain (timestamps in milliseconds)
  const domain = useMemo(() => {
    // Use exact timestamps from chart - this is the single source of truth
    if (axisConfig?.domainStartTs && axisConfig?.domainEndTs) {
      return { 
        startTs: axisConfig.domainStartTs, 
        endTs: axisConfig.domainEndTs 
      };
    }
    
    // Fallback if no chart config
    if (supplements.length === 0) {
      const now = Date.now();
      const yearAgo = now - (365 * 24 * 60 * 60 * 1000);
      return { startTs: yearAgo, endTs: now };
    }
    
    const timestamps = supplements.flatMap(s => [
      parseDate(s.start_date)?.getTime(),
      s.end_date ? parseDate(s.end_date)?.getTime() : Date.now()
    ]).filter(Boolean);
    
    return { 
      startTs: Math.min(...timestamps), 
      endTs: Math.max(...timestamps) 
    };
  }, [axisConfig, supplements]);

  // Use chart's tick labels directly for perfect alignment
  // The chart passes its exact ticks with pre-formatted labels
  const ticks = useMemo(() => {
    // If chart provided pre-formatted ticks, use them directly
    if (axisConfig?.ticks?.length > 0 && axisConfig.ticks[0]?.label) {
      return axisConfig.ticks.map(tick => ({
        ...tick,
        visible: true
      }));
    }
    
    // Fallback: Generate time-based ticks if no chart ticks provided
    if (!domain.startTs || !domain.endTs) return [];
    
    const range = domain.endTs - domain.startTs;
    if (range <= 0) return [];
    
    // Generate evenly spaced time ticks
    const tickCount = isMobile ? 3 : 5;
    const generatedTicks = [];
    
    for (let i = 0; i <= tickCount; i++) {
      const timestamp = domain.startTs + (range * i / tickCount);
      const label = formatChartDateShort(timestamp, country);
      generatedTicks.push({
        timestamp,
        label,
        position: i / tickCount,
        visible: true
      });
    }
    
    return generatedTicks;
  }, [axisConfig?.ticks, domain, country, isMobile]);

  // Memoized "now" for ongoing items
  const nowTimestamp = useMemo(() => new Date().getTime(), []);

  // Filter supplements
  const filteredSupplements = useMemo(() => {
    return supplements.filter(s => activeCategories.includes(s.category || 'supplement'));
  }, [supplements, activeCategories]);

  const toggleCategory = (category) => {
    setActiveCategories(prev => {
      if (prev.includes(category)) {
        if (prev.length === 1) return prev;
        return prev.filter(c => c !== category);
      }
      return [...prev, category];
    });
  };

  // Calculate hovered date position using exact timestamps
  const hoveredPosition = useMemo(() => {
    if (!hoveredDate || !domain.startTs || !domain.endTs) return null;
    
    const hoveredTs = new Date(hoveredDate).getTime();
    const totalRange = domain.endTs - domain.startTs;
    if (totalRange <= 0) return null;
    
    const position = ((hoveredTs - domain.startTs) / totalRange) * 100;
    if (position < 0 || position > 100) return null;
    
    return position;
  }, [hoveredDate, domain]);

  // Matching margin from chart (left padding aligns with Y-axis)
  const chartMargin = axisConfig?.margin || { left: 45, right: 20 };

  if (supplements.length === 0) {
    return (
      <div 
        className="bg-slate-50 rounded-b-xl border-x border-b border-slate-200 py-4 text-center"
        style={{ marginLeft: 0, marginRight: 0 }}
      >
        <div className="text-slate-400 text-xs">
          <Leaf className="h-5 w-5 mx-auto mb-1 opacity-50" />
          No treatments logged yet.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 rounded-b-xl border-x border-b border-slate-200 overflow-hidden">
      {/* Category filters - compact layout */}
      <div className="px-3 py-1.5 border-b border-slate-200/80 bg-white/40">
        <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1.5 text-right sm:text-left">
          Treatments
        </span>
        <div className="flex gap-1.5 justify-end sm:justify-start">
          {/* Always show the 4 main categories: vitamin, supplement, medication, other */}
          {['vitamin', 'supplement', 'medication', 'other'].map((key) => {
            const config = CATEGORIES[key];
            const isActive = activeCategories.includes(key);
            const count = supplements.filter(s => (s.category || 'supplement') === key).length;
            // Abbreviate supplement label
            const shortLabel = key === 'supplement' ? 'Supp' : key === 'medication' ? 'Meds' : config.label;
            
            return (
              <button
                key={key}
                onClick={() => count > 0 ? toggleCategory(key) : null}
                disabled={count === 0}
                className={`
                  flex items-center justify-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all whitespace-nowrap
                  ${count === 0 ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                  ${!isActive && count > 0 ? 'ring-1 ring-inset ring-white/30' : ''}
                `}
                style={{ 
                  backgroundColor: config.bgColor, 
                  color: config.textColor,
                  opacity: count === 0 ? 0.5 : (isActive ? 1 : 0.7)
                }}
              >
                {shortLabel}
                <span 
                  className="px-1.5 rounded-full text-[9px]"
                  style={{ backgroundColor: 'rgba(255,255,255,0.3)', color: config.textColor }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline content with matching padding */}
      <div className="relative">
        {/* X-axis labels - Must show same dates as chart above for alignment */}
        <div 
          className="relative h-5 border-b border-slate-200/60 overflow-hidden"
          style={{ 
            marginLeft: chartMargin.left, 
            marginRight: chartMargin.right 
          }}
        >
          {ticks.map((tick, idx) => {
            // Skip rendering hidden ticks (but keep position for alignment)
            if (tick.visible === false) return null;
            
            const totalVisibleTicks = ticks.filter(t => t.visible !== false).length;
            const visibleIndex = ticks.filter((t, i) => i < idx && t.visible !== false).length;
            const isFirst = visibleIndex === 0;
            const isLast = visibleIndex === totalVisibleTicks - 1;
            
            return (
              <span 
                key={idx}
                className={`absolute text-[9px] sm:text-[10px] text-slate-500 top-0.5 whitespace-nowrap
                  ${isFirst ? 'translate-x-0' : isLast ? '-translate-x-full' : '-translate-x-1/2'}
                `}
                style={{ left: `${tick.position * 100}%` }}
              >
                {tick.label}
              </span>
            );
          })}
        </div>

        {/* Vertical guide line for hovered chart date */}
        {hoveredPosition !== null && (
          <div 
            className="absolute top-5 bottom-0 w-px bg-slate-400/50 z-10 pointer-events-none"
            style={{ left: `calc(${chartMargin.left}px + ${hoveredPosition}% * (100% - ${chartMargin.left + chartMargin.right}px) / 100%)` }}
          />
        )}

        {/* Timeline rows */}
        <div 
          className="overflow-y-auto py-1"
          style={{ 
            maxHeight: `${maxHeight}px`,
            paddingLeft: chartMargin.left,
            paddingRight: chartMargin.right
          }}
        >
          {filteredSupplements.map((item) => {
            const category = CATEGORIES[item.category || 'supplement'];
            const Icon = category.icon;
            
            // Use LINEAR TIME positioning - same scale as chart X-axis
            const barStyle = calculateBarStyleLinear(
              item.start_date, 
              item.end_date, 
              domain.startTs, 
              domain.endTs
            );
            
            // Skip treatments that fall entirely outside the chart range
            if (!barStyle.visible) return null;
            
            const isExpanded = expandedId === item.id;
            const isOngoing = !item.end_date;
            const isEnded = !!item.end_date;
            const isSelected = selectedTreatment?.id === item.id;
            
            // Check if treatment started before visible window
            const startTs = parseDate(item.start_date)?.getTime();
            const isClippedLeft = startTs && startTs < domain.startTs;
            
            // Check if treatment was active at hovered date
            const isHighlighted = hoveredDate && (() => {
              const date = new Date(hoveredDate).getTime();
              const start = parseDate(item.start_date)?.getTime();
              const end = item.end_date ? parseDate(item.end_date).getTime() : nowTimestamp;
              return start && date >= start && date <= end;
            })();

            // Handle treatment click for chart overlay
            const handleTreatmentClick = (e) => {
              e.stopPropagation();
              if (onTreatmentSelect) {
                onTreatmentSelect(item);
              }
              // Still toggle expanded for details
              setExpandedId(isExpanded ? null : item.id);
            };

            return (
              <div key={item.id} className="mb-0.5">
                {/* Timeline Row */}
                <div 
                  className={`
                    relative h-6 cursor-pointer rounded transition-colors group
                    ${isHighlighted ? 'bg-slate-200/60' : 'hover:bg-slate-100/60'}
                    ${isSelected ? 'bg-slate-200/80' : ''}
                  `}
                  onClick={handleTreatmentClick}
                  onMouseEnter={() => onHoverDate && onHoverDate(item.start_date)}
                  onMouseLeave={() => onHoverDate && onHoverDate(null)}
                >
                  {/* Clipped indicator for treatments that started before visible window */}
                  {isClippedLeft && (
                    <span 
                      className="absolute left-0 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400"
                      title="Started earlier"
                    >
                      ‹
                    </span>
                  )}
                  
                  {/* The colored bar */}
                  <div 
                    className={`
                      absolute top-0.5 h-5 rounded-full flex items-center px-1.5 gap-1 transition-all
                      ${isHighlighted ? 'ring-2 ring-slate-400' : ''}
                      ${isSelected ? 'ring-2 ring-offset-1' : ''}
                    `}
                    style={{
                      left: barStyle.left,
                      width: barStyle.isThin ? '20px' : barStyle.width,
                      backgroundColor: isSelected ? category.iconColor : category.bgColor,
                      opacity: isEnded ? 0.85 : 1,
                      '--tw-ring-color': isSelected ? category.iconColor : undefined
                    }}
                  >
                    <Icon 
                      className="h-3 w-3 flex-shrink-0" 
                      style={{ color: isSelected ? 'white' : category.iconColor }} 
                    />
                    {/* Name INSIDE bar only if wide enough */}
                    {!barStyle.isThin && (
                      <span 
                        className="text-[11px] font-semibold truncate"
                        style={{ color: isSelected ? 'white' : category.textColor }}
                      >
                        {item.name}
                      </span>
                    )}
                    {!barStyle.isThin && isOngoing && (
                      <span className="text-[10px] flex-shrink-0" style={{ color: isSelected ? 'white' : category.textColor, opacity: 0.7 }}>→</span>
                    )}
                  </div>
                  
                  {/* Label for thin bars - always visible, positioned to LEFT of dot for right-side items */}
                  {barStyle.isThin && (() => {
                    const leftPercent = parseFloat(barStyle.left);
                    const isRightSide = leftPercent > 50;
                    const bgColor = category?.bgColor || '#3FAE7F';
                    
                    return (
                      <span 
                        style={{ 
                          position: 'absolute',
                          top: '2px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          right: isRightSide ? '28px' : 'auto',
                          left: isRightSide ? 'auto' : `calc(${barStyle.left} + 24px)`,
                          color: bgColor,
                          fontSize: '11px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap',
                          pointerEvents: 'none'
                        }}
                      >
                        {item.name}
                      </span>
                    );
                  })()}
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="mb-1 p-2 bg-white rounded border border-slate-200 text-xs shadow-sm">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Icon className="h-3 w-3" style={{ color: category.bgColor }} />
                      <span className="font-semibold text-slate-800">{item.name}</span>
                      <span 
                        className="px-1.5 py-0.5 rounded-full text-[9px] font-medium"
                        style={{ backgroundColor: category.bgColor, color: category.textColor }}
                      >
                        {category.label}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-slate-600 text-[11px]">
                      <div>
                        <span className="text-slate-400">Started:</span>{' '}
                        <span className="font-medium">{formatDate(item.start_date)}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Ended:</span>{' '}
                        <span className="font-medium">{formatDate(item.end_date)}</span>
                      </div>
                      {item.dosage && (
                        <div>
                          <span className="text-slate-400">Dosage:</span>{' '}
                          <span className="font-medium">{item.dosage}</span>
                        </div>
                      )}
                      {item.frequency && (
                        <div>
                          <span className="text-slate-400">Freq:</span>{' '}
                          <span className="font-medium">{item.frequency}</span>
                        </div>
                      )}
                    </div>
                    
                    {item.notes && (
                      <div className="mt-1.5 pt-1.5 border-t border-slate-100 text-slate-500 text-[10px]">
                        {item.notes}
                      </div>
                    )}
                    
                    {/* Mandatory disclaimer in detail view */}
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <p className="text-[9px] text-slate-400 leading-relaxed">
                        Trends shown are informational only. Changes in blood counts happen naturally in CLL and do not show whether a treatment is working. Never start or stop any medication or supplement without your haematologist.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="px-3 py-1.5 border-t border-slate-200/60 bg-white/30">
        <p className="text-[9px] text-slate-400 text-center">
          Observational only • Does not imply causation
        </p>
      </div>
    </div>
  );
};

export default SupplementTimeline;
